import type { AxiosResponse } from "axios";

import type {
  NamirialOneshotFinishPayload,
  NamirialOneshotStartPayload,
  NamirialSignboxStartPayload,
  ResponseNamirialSignature,
} from "../types/namirial";
import api from "../utils/AxiosInterceptor";

export const signatureBatchLink = async (
  hash: string,
): Promise<AxiosResponse<ResponseSignatures>> => {
  return api.get(`/api/v1/signature-batch/link/${hash}`, {
    headers: { "Content-Type": "application/json" },
  });
};

export interface CheckAccessResponse {
  signatureProcessId: string;
  signerId: string;
}

export const signatureSingleLink = async (
  linkCode: string,
): Promise<AxiosResponse<ResponseSignatures>> => {
  return api.get(`/api/v1/signatures/link/${linkCode}`, {
    headers: { "Content-Type": "application/json" },
  });
};

export const signatureCheckAccess = async (
  linkCode: string,
  lastDocumentDigits: string,
): Promise<AxiosResponse<CheckAccessResponse>> => {
  return api.post(
    `/api/v1/signatures/link/${linkCode}/check-access`,
    { lastDocumentDigits },
    {
      headers: { "Content-Type": "application/json" },
    },
  );
};

export const signatureSignOtp = async (
  signatureId: string,
  otp: string,
): Promise<AxiosResponse<TemporaryUrl>> => {
  return api.put(
    `/api/v1/signature-batch/${signatureId}/sign`,
    { otp },
    {
      headers: { "Content-Type": "application/json" },
    },
  );
};

export const signatureSingleSignOtp = async (
  signatureId: string,
  otp: string,
): Promise<AxiosResponse<ResponseSignatures>> => {
  return api.put(
    `/api/v1/signatures/${signatureId}/sign`,
    { otp },
    {
      headers: { "Content-Type": "application/json" },
    },
  );
};

export const signatureSingleResendOtp = async (
  signatureId: string,
  identifier: string,
): Promise<AxiosResponse<void>> => {
  return api.post(
    `/api/v1/otp/${signatureId}/resend-to`,
    { identifier },
    {
      headers: { "Content-Type": "application/json" },
    },
  );
};

export const signatureDigitalStart = async (
  envelopeId: string,
  certificateBase64: string,
  linkCode: string,
): Promise<AxiosResponse<ResponseSignatureDigital>> => {
  return api.post(
    `/api/v1/signature-batch/${envelopeId}/digital/start`,
    {
      certificateBase64,
      hasCarimboTempo: false,
      linkCode,
    },
    {
      headers: { "Content-Type": "application/json" },
    },
  );
};

export const signatureDigitalGetStatus = async (
  envelopeId: string,
  configId: string,
): Promise<AxiosResponse<ResponseSignatureDigital>> => {
  return api.get(`/api/v1/signature-batch/${envelopeId}/digital/${configId}`, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const signatureDigitalFinish = async (
  envelopeId: string,
  configId: string,
  files: FilesSignatureDigitalFinish[],
): Promise<AxiosResponse<ResponseSignatureDigital>> => {
  return api.post(
    `/api/v1/signature-batch/${envelopeId}/digital/${configId}/finish`,
    {
      files,
    },
    {
      headers: { "Content-Type": "application/json" },
    },
  );
};

// ---- Namirial (Paraguay) APIs ----

export const namirialSignboxStart = async (
  envelopeId: string,
  payload: NamirialSignboxStartPayload,
): Promise<AxiosResponse<ResponseNamirialSignature>> => {
  return api.post<ResponseNamirialSignature>(
    `/api/v1/signature-batch/${envelopeId}/namirial/signbox/start`,
    payload,
    {
      headers: { "Content-Type": "application/json" },
    },
  );
};

export const namirialOneshotStart = async (
  envelopeId: string,
  payload: NamirialOneshotStartPayload,
): Promise<AxiosResponse<ResponseNamirialSignature>> => {
  return api.post<ResponseNamirialSignature>(
    `/api/v1/signature-batch/${envelopeId}/namirial/oneshot/start`,
    payload,
    {
      headers: { "Content-Type": "application/json" },
    },
  );
};

export const namirialGetStatus = async (
  envelopeId: string,
  configId: string,
): Promise<AxiosResponse<ResponseNamirialSignature>> => {
  return api.get<ResponseNamirialSignature>(
    `/api/v1/signature-batch/${envelopeId}/namirial/${configId}`,
    {
      headers: { "Content-Type": "application/json" },
    },
  );
};

export const namirialOneshotFinish = async (
  envelopeId: string,
  configId: string,
  payload: NamirialOneshotFinishPayload,
): Promise<AxiosResponse<ResponseNamirialSignature>> => {
  return api.post<ResponseNamirialSignature>(
    `/api/v1/signature-batch/${envelopeId}/namirial/oneshot/${configId}/finish`,
    payload,
    {
      headers: { "Content-Type": "application/json" },
    },
  );
};

export const namirialOneshotGenerateOtp = async (
  envelopeId: string,
  configId: string,
): Promise<void> => {
  await api.post<ResponseNamirialSignature>(
    `/api/v1/signature-batch/${envelopeId}/namirial/oneshot/${configId}/generate-otp`,
    {},
    {
      headers: { "Content-Type": "application/json" },
    },
  );
};

export const namirialOneshotResendOtp = async (
  envelopeId: string,
  configId: string,
): Promise<void> => {
  await api.post<ResponseNamirialSignature>(
    `/api/v1/signature-batch/${envelopeId}/namirial/oneshot/${configId}/resend-otp`,
    {},
    {
      headers: { "Content-Type": "application/json" },
    },
  );
};

export const namirialGetByLinkCode = async (
  envelopeId: string,
  linkCode: string,
): Promise<AxiosResponse<ResponseNamirialSignature>> => {
  return api.get<ResponseNamirialSignature>(
    `/api/v1/signature-batch/${envelopeId}/namirial/link-code/${linkCode}`,
    {
      headers: { "Content-Type": "application/json" },
    },
  );
};

/** Corpo da resposta Namirial (evita `any` nos componentes com o parser ESLint). */
export const namirialGetByLinkCodeData = async (
  envelopeId: string,
  linkCode: string,
): Promise<ResponseNamirialSignature> => {
  const { data } = await namirialGetByLinkCode(envelopeId, linkCode);
  return data[0] as ResponseNamirialSignature;
};

export const namirialOneshotStartData = async (
  envelopeId: string,
  payload: NamirialOneshotStartPayload,
): Promise<ResponseNamirialSignature> => {
  const { data } = await namirialOneshotStart(envelopeId, payload);
  return data;
};

export const signatureObserverLink = async (
  code: string,
  email: string,
): Promise<AxiosResponse<ResponseSignatures>> => {
  return api.post(
    `/api/v1/signature-batch/observer/link/${code}`,
    { email },
    {
      headers: { "Content-Type": "application/json" },
    },
  );
};

export const downloadObserverEnvelopeZip = async (
  code: string,
  email: string,
): Promise<AxiosResponse<Blob>> => {
  return api.post(
    `/api/v1/signature-batch/observer/link/${code}/download-zip`,
    { email },
    {
      responseType: "blob",
      headers: { "Content-Type": "application/json" },
    },
  );
};

export const signatureBatchResendOtp = async (
  signatureId: string,
  identifier: string,
): Promise<AxiosResponse<void>> => {
  return api.post(
    `/api/v1/signature-batch/${signatureId}/resend-to`,
    { identifier },
    {
      headers: { "Content-Type": "application/json" },
    },
  );
};

export interface RefuseSignaturePayload {
  linkCode: string;
  reason: string;
}

export const refuseSignature = async (
  signatureId: string,
  payload: RefuseSignaturePayload,
): Promise<AxiosResponse<void>> => {
  return api.post(
    `/api/v1/signature-batch/${signatureId}/refuse`,
    payload,
    {
      headers: { "Content-Type": "application/json" },
    },
  );
};
